import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogDescription,
  DialogTitle,
} from './common/Catalyst/dialog';
import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
} from './common/Catalyst/fieldset';
import { FieldPicker } from './common/Custom/FieldPicker';
import { Input } from './common/Catalyst/input';
import { Button } from './common/Catalyst/button';
import { Text } from './common/Catalyst/text';
import { Badge } from './common/Catalyst/badge';
import ShowAlert from './ShowAlert';
import LoadingSpinner from './LoadingSpinner';
import { bankService } from './clients/ApiClients';
import { TransferPreviewReturn } from './clients/rust-api-client';
import { formatCurrency, getBankErrorParts } from './utils/bankHelpers';

interface TransferDialogProps {
  open: boolean;
  onClose: () => void;
  hoaId: string;
  /** Called after a transfer reaches a terminal/submitted state, to refresh
   * the history list. */
  onCompleted: () => void;
}

interface AlertState {
  title: string;
  message: string;
}

// A picker option — one of the HOA's assigned accounts.
interface AcctOption {
  id: string;
  accountNumber: string;
  purpose?: string | null;
  balance?: number | null;
}

const purposeLabel = (purpose?: string | null): string =>
  purpose ? purpose.charAt(0).toUpperCase() + purpose.slice(1) : 'Account';

const acctLabel = (a: AcctOption): string => {
  const base = `${purposeLabel(a.purpose)} — ${a.accountNumber}`;
  return a.balance !== null && a.balance !== undefined
    ? `${base} (${formatCurrency(a.balance)})`
    : base;
};

// Title for an error surfaced from a bank call, keyed off the structured
// error `code` / HTTP status (see backend BankErrorBody).
const bankErrorTitle = (
  status?: number,
  code?: string,
  fallback = "Couldn't complete the transfer"
): string => {
  if (code === 'bank_unreachable' || status === 503)
    return 'Bank temporarily unavailable';
  if (code === 'bank_error' || status === 502) return 'Bank error';
  if (status === 400) return 'Transfer not allowed';
  return fallback;
};

const TransferDialog: React.FC<TransferDialogProps> = ({
  open,
  onClose,
  hoaId,
  onCompleted,
}) => {
  const [accounts, setAccounts] = useState<AcctOption[]>([]);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [accountsError, setAccountsError] = useState<string | null>(null);

  const [sourceId, setSourceId] = useState('');
  const [destId, setDestId] = useState('');
  const [amount, setAmount] = useState('');

  // When set, we're on the confirmation step for this previewed transfer.
  const [preview, setPreview] = useState<TransferPreviewReturn | null>(null);
  const [error, setError] = useState<AlertState | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const resetForm = useCallback(() => {
    setSourceId('');
    setDestId('');
    setAmount('');
    setPreview(null);
    setError(null);
  }, []);

  // Load the HOA's assigned accounts (live, for balances) whenever opened.
  useEffect(() => {
    if (!open) return;
    resetForm();
    let cancelled = false;
    (async () => {
      setAccountsLoading(true);
      setAccountsError(null);
      try {
        const resp = await bankService.getHoaAccountsLive({ hoaId });
        if (cancelled) return;
        setAccounts(
          resp.data.accounts.map((a) => ({
            id: a.id,
            accountNumber: a.account_number,
            purpose: a.purpose,
            balance: a.current_balance,
          }))
        );
      } catch (err) {
        if (cancelled) return;
        const { status, body } = getBankErrorParts(err);
        setAccounts([]);
        setAccountsError(
          status === 502 || status === 503
            ? 'The bank is unreachable from this environment, so transfers cannot be initiated here. Use the deployed environment.'
            : body || "Couldn't load this HOA’s accounts."
        );
      } finally {
        if (!cancelled) setAccountsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, hoaId, resetForm]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const handlePreview = async () => {
    setError(null);
    if (!sourceId || !destId) {
      setError({
        title: 'Select both accounts',
        message: 'Choose a source and a destination account.',
      });
      return;
    }
    if (sourceId === destId) {
      setError({
        title: 'Different accounts required',
        message: 'Source and destination must be different accounts.',
      });
      return;
    }
    const amt = Number(amount);
    if (!amount || isNaN(amt) || amt <= 0) {
      setError({
        title: 'Enter an amount',
        message: 'Amount must be greater than zero.',
      });
      return;
    }
    try {
      setSubmitting(true);
      const resp = await bankService.previewHoaTransfer({
        hoaId,
        transferPreviewRequest: {
          source_hoa_account_id: sourceId,
          dest_hoa_account_id: destId,
          amount: amount.trim(),
        },
      });
      setPreview(resp.data);
    } catch (err) {
      const { status, code, body } = getBankErrorParts(err);
      setError({
        title: bankErrorTitle(status, code, "Couldn't preview the transfer"),
        message: body || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!preview) return;
    try {
      setSubmitting(true);
      setError(null);
      const resp = await bankService.confirmHoaTransfer({
        hoaId,
        id: preview.transfer_id,
      });
      const status = resp.data.status;
      if (status === 'completed') {
        onCompleted();
        onClose();
      } else if (status === 'failed') {
        // Terminal failure — the reference is spent; a retry needs a fresh
        // preview, so drop back to the form and surface the bank's reason.
        onCompleted();
        setPreview(null);
        setError({
          title: 'Transfer failed',
          message:
            resp.data.error_message ||
            'The bank did not complete the transfer.',
        });
      } else {
        // pending — submitted but not yet confirmed (e.g. bank slow). It will
        // reconcile; leave the user on the confirm step with a note.
        onCompleted();
        setError({
          title: 'Transfer submitted',
          message:
            'The transfer was submitted but not yet confirmed. Check the transfers list shortly.',
        });
      }
    } catch (err) {
      const { status, code, body } = getBankErrorParts(err);
      setError({
        title: bankErrorTitle(status, code),
        message: body || 'Please try again.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const notEnoughAccounts = !accountsLoading && !accountsError && accounts.length < 2;

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>
        {preview ? 'Confirm Transfer' : 'New Transfer'}
      </DialogTitle>
      <DialogDescription>
        {preview
          ? 'Review the details below, then confirm to move the money.'
          : 'Move money between two of this HOA’s bank accounts.'}
      </DialogDescription>
      <DialogBody>
        {error && (
          <div className="mb-4">
            <ShowAlert
              alertType="error"
              alertTitle={error.title}
              alertMessage={error.message}
              isVisible={!!error}
              onClose={() => setError(null)}
              timeout={12000}
              isInline={true}
            />
          </div>
        )}

        {accountsLoading ? (
          <LoadingSpinner />
        ) : accountsError ? (
          <ShowAlert
            alertType="error"
            alertTitle="Accounts unavailable"
            alertMessage={accountsError}
            isVisible={!!accountsError}
            onClose={() => setAccountsError(null)}
            timeout={12000}
            isInline={true}
          />
        ) : notEnoughAccounts ? (
          <Text>
            At least two bank accounts must be assigned to this HOA before you
            can transfer between them.
          </Text>
        ) : preview ? (
          // --- Confirmation step -------------------------------------------
          <div className="space-y-3">
            <div className="rounded-md bg-stone-200 dark:bg-zinc-700 px-4 py-3">
              <div className="flex items-center justify-between">
                <Text>
                  <span className="font-medium">
                    {purposeLabel(preview.source_purpose)}
                  </span>{' '}
                  · {preview.source_account_number}
                </Text>
                <Badge color="sky">→</Badge>
                <Text>
                  <span className="font-medium">
                    {purposeLabel(preview.dest_purpose)}
                  </span>{' '}
                  · {preview.dest_account_number}
                </Text>
              </div>
            </div>
            <div className="flex items-baseline justify-between">
              <Text>Amount</Text>
              <span className="text-lg font-semibold tabular-nums">
                {formatCurrency(Number(preview.amount))}
              </span>
            </div>
            {preview.source_current_balance !== null &&
              preview.source_current_balance !== undefined && (
                <div className="flex items-baseline justify-between">
                  <Text>Source balance now</Text>
                  <span className="tabular-nums">
                    {formatCurrency(preview.source_current_balance)}
                  </span>
                </div>
              )}
            {preview.source_projected_balance !== null &&
              preview.source_projected_balance !== undefined && (
                <div className="flex items-baseline justify-between">
                  <Text>Source balance after</Text>
                  <span className="tabular-nums">
                    {formatCurrency(preview.source_projected_balance)}
                  </span>
                </div>
              )}
            <Text className="text-xs">
              Maximum per transfer: {formatCurrency(Number(preview.max_amount))}
            </Text>
          </div>
        ) : (
          // --- Form step ---------------------------------------------------
          <Fieldset>
            <FieldGroup>
              <Field>
                <Label>From (source)</Label>
                <FieldPicker
                  label="From (source)"
                  value={sourceId}
                  onChange={setSourceId}
                  placeholder="Select an account"
                  options={accounts
                    .filter((a) => a.id !== destId)
                    .map((a) => ({ value: a.id, label: acctLabel(a) }))}
                />
              </Field>
              <Field>
                <Label>To (destination)</Label>
                <FieldPicker
                  label="To (destination)"
                  value={destId}
                  onChange={setDestId}
                  placeholder="Select an account"
                  options={accounts
                    .filter((a) => a.id !== sourceId)
                    .map((a) => ({ value: a.id, label: acctLabel(a) }))}
                />
              </Field>
              <Field>
                <Label>Amount (USD)</Label>
                <Input
                  name="amount"
                  type="text"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Field>
            </FieldGroup>
          </Fieldset>
        )}
      </DialogBody>
      <DialogActions>
        {preview ? (
          <>
            <Button
              plain
              onClick={() => setPreview(null)}
              disabled={submitting}
            >
              Back
            </Button>
            <Button color="sky" onClick={handleConfirm} disabled={submitting}>
              {submitting ? 'Transferring…' : 'Confirm transfer'}
            </Button>
          </>
        ) : (
          <>
            <Button plain onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button
              color="sky"
              onClick={handlePreview}
              disabled={
                submitting ||
                accountsLoading ||
                !!accountsError ||
                notEnoughAccounts
              }
            >
              {submitting ? 'Checking…' : 'Continue'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default TransferDialog;
